import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchMapData, fetchTopologyAPI } from './mapAPI';
import { RootState } from '../../state/store';

export interface StateResponse {
  value: number;
  value_change: number;
}

export interface MapResponse {
  state_date: {
    [key: string]: StateResponse;
  };
  all_data: {
    value: number;
    value_change: number;
  };
}

export interface StateMetrics {
  name: string;
  data: number;
  value: number | null;
  percentage: number | null;
  colorValue: number;
  color: string;
}

export interface TopologyData {
  [key: string]: any;
}

export interface MapState {
  regionData: StateMetrics[];
  allData: { value: number; value_change: number };
  loading: boolean;
  topology: TopologyData | null;
  updatedStates: string[];
}

function interpolateColor(color1: string, color2: string, factor: number): string {
  factor = Math.max(0, Math.min(1, factor));

  const hexToRgb = (hex: string) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];

  const rgbToHex = (rgb: number[]) =>
    `#${rgb
      .map((c) => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()}`;

  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return rgbToHex([r, g, b]);
}

function calculateColor(value: number, minValue: number, maxValue: number): string {
  if (value < 0) {
    const factor = Math.max(0, Math.min(1, (value - minValue) / (0 - minValue)));
    return interpolateColor('#ED4949', '#FFE9EA', factor);
  } else if (value > 0) {
    const factor = Math.max(0, Math.min(1, value / maxValue));
    return interpolateColor('#E2FFF7', '#089D72', factor);
  } else {
    return '#FFFFFF';
  }
}

const initialState: MapState = {
  regionData: [],
  allData: { value: 0, value_change: 0 },
  loading: false,
  topology: null,
  updatedStates: [],
};

export const fetchMapMetrics = createAsyncThunk<any, any, { rejectValue: string }>(
  'map/fetchMapMetrics',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchMapData(payload);
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch map metrics');
    }
  },
);

export const fetchTopologyData = createAsyncThunk<TopologyData, void>('map/fetchTopology', async () => {
  const response = await fetchTopologyAPI();
  return response;
});

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setUpdatedStates: (state, action: PayloadAction<string[]>) => {
      state.updatedStates = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMapMetrics.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchMapMetrics.fulfilled, (state, action: PayloadAction<MapResponse>) => {
      state.loading = false;

      if (!action.payload) {
        return;
      }

      const { state_date, all_data } = action.payload;
      const allValues = Object.values(state_date).map((data) => data.value_change);
      const minValue = Math.min(...allValues.filter((v) => v < 0));
      const maxValue = Math.max(...allValues.filter((v) => v > 0));

      state.regionData = [];

      const missingStates = [
        'Andaman and Nicobar',
        'Aksai Chin',
        'Balochistan',
        'Uttarakhand',
        'Puducherry',
        'Lakshadweep',
        'Himachal Pradesh',
      ];

      Object.entries(state_date).forEach(([name, { value, value_change }]) => {
        if (name === 'Assam & North East') {
          state.regionData.push({
            name: 'Assam',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
          const northEastStates = [
            'Arunanchal Pradesh',
            'Manipur',
            'Mizoram',
            'Nagaland',
            'Tripura',
            'Meghalaya',
            'Sikkim',
          ];

          northEastStates.forEach((stateName) => {
            state.regionData.push({
              name: stateName,
              data: value,
              value: value_change,
              percentage: null,
              colorValue: value,
              color: calculateColor(value_change, minValue, maxValue),
            });
          });
        } else if (name === 'Maharashtra + Goa') {
          state.regionData.push({
            name: 'Maharashtra',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
          state.regionData.push({
            name: 'Goa',
            data: value,
            value: value_change,
            percentage: null,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else if (name === 'Delhi/NCR') {
          state.regionData.push({
            name: 'NCT of Delhi',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else if (name === 'Jammu') {
          state.regionData.push({
            name: 'Jammu and Kashmir',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
          state.regionData.push({
            name: 'Himachal Pradesh',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else if (name === 'Orissa') {
          state.regionData.push({
            name: 'Odisha',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else if (name === 'Uttar Pradesh') {
          state.regionData.push({
            name: 'Uttarakhand',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
          state.regionData.push({
            name: 'Uttar Pradesh',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else if (name === 'Tamil Nadu') {
          state.regionData.push({
            name: 'Puducherry',
            data: value,
            value: value_change,
            percentage: null,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
          state.regionData.push({
            name: 'Tamil Nadu',
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        } else {
          state.regionData.push({
            name,
            data: value,
            value: value_change,
            percentage: value_change,
            colorValue: value,
            color: calculateColor(value_change, minValue, maxValue),
          });
        }
      });

      missingStates.forEach((missingState) => {
        if (!state.regionData.some((state) => state.name === missingState)) {
          state.regionData.push({
            name: missingState,
            data: 2000,
            value: null,
            percentage: null,
            colorValue: 2000,
            color: calculateColor(2000, minValue, maxValue),
          });
        }
      });

      state.allData = all_data;
    });

    builder.addCase(fetchMapMetrics.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(fetchTopologyData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchTopologyData.fulfilled, (state, action) => {
      state.loading = false;
      state.topology = action.payload;
    });
    builder.addCase(fetchTopologyData.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { setUpdatedStates } = mapSlice.actions;

export const selectRegionData = (state: RootState) => state.map.regionData;

export const selectAllData = (state: RootState) => state.map.allData;

export const selectMapLoading = (state: RootState) => state.map.loading;

export const selectTopology = (state: RootState) => state.map.topology;

export const selectUpdatedStates = (state: RootState) => state.map.updatedStates;

export default mapSlice.reducer;
